import React, { useState, useEffect } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
} from "@mui/material";

const GrandPrixScores = ({ scoreHistory }) => {
    const players = scoreHistory.racePlayers;
    const [sort, setSort] = useState({ field: null, order: 'asc' });

    useEffect(() => {
        // Set initial sort order for "Total Score" column
        setSort({ field: 'totalScore', order: 'desc' });
    }, []);

    const handleSort = (field) => {
        setSort((prevSort) => ({
            field,
            order: prevSort.field === field && prevSort.order === 'asc' ? 'desc' : 'asc',
        }));
    };

    // Step 1: Assign ranks based on totalScore first
    const rankedPlayers = Object.values(players)
        .sort((a, b) => b.totalScore - a.totalScore)  // Sort by totalScore
        .map((player, index) => ({
            ...player,
            rank: index + 1, // Assign rank based on position after sorting by totalScore
        }));

    // Step 2: Sort the players based on the selected field
    const sortedPlayers = rankedPlayers
        .sort((a, b) => {
            const order = sort.order === 'asc' ? 1 : -1;
            if (sort.field === 'fullName') {
                return order * a.fullName.localeCompare(b.fullName);
            } else if (sort.field === 'firsts' || sort.field === 'lasts' || sort.field === 'totalScore') {
                return order * (a[sort.field] - b[sort.field]);
            }
            return 0; // Default catch-all
        });

    return (
        <TableContainer
            sx={{
                width: '100%',  // Force the table to fit within the modal width
                border: 1,
                borderColor: "black",
                overflow: 'hidden',  // Disable scrolling entirely
                fontSize: '0.8rem',
                '& th, & td': {
                    fontSize: '0.7rem',
                    p: 1,
                    fontWeight: 'bold', // Apply font size to table cells and headers
                },
            }}
        >
            <Table size="small" aria-label="a dense table">
                <TableHead>
                    <TableRow sx={{ backgroundColor: 'primary.light', border: '2px solid black' }}>
                        <TableCell>
                            <TableSortLabel
                                active={sort.field === 'fullName'}
                                direction={sort.field === 'fullName' ? sort.order : 'asc'}
                                onClick={() => handleSort('fullName')}
                            >
                                Name
                            </TableSortLabel>
                        </TableCell>
                        <TableCell>
                            <TableSortLabel
                                active={sort.field === 'firsts'}
                                direction={sort.field === 'firsts' ? sort.order : 'asc'}
                                onClick={() => handleSort('firsts')}
                            >
                                Firsts
                            </TableSortLabel>
                        </TableCell>
                        <TableCell>
                            <TableSortLabel
                                active={sort.field === 'lasts'}
                                direction={sort.field === 'lasts' ? sort.order : 'asc'}
                                onClick={() => handleSort('lasts')}
                            >
                                Lasts
                            </TableSortLabel>
                        </TableCell>
                        <TableCell>
                            <TableSortLabel
                                active={sort.field === 'totalScore'}
                                direction={sort.field === 'totalScore' ? sort.order : 'asc'}
                                onClick={() => handleSort('totalScore')}
                            >
                                Score
                            </TableSortLabel>
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {sortedPlayers.map((player, index) => {
                        // Use a combination of player ID and index for a unique key
                        const uniqueKey = `player-${player.playerId || index}-${index}`;
                        return (
                            <TableRow
                                key={uniqueKey}
                                sx={{
                                    backgroundImage: player.rank === 1 ?
                                        'linear-gradient(45deg,#d4af37 5%,#efd69d 10%,#e7c97c 30%,#debc5b 50%,#e7c97c 70%,#efd69d 80%,#d4af37 95%)' : // Gold gradient
                                        player.rank === 2 ?
                                            'linear-gradient(45deg,#999 5%,#fff 10%,#ccc 30%,#ddd 50%,#ccc 70%,#fff 80%,#999 95%)' : // Silver gradient
                                            player.rank === 3 ?
                                                'linear-gradient(45deg,#CD7F32 5%,#f29d49 10%,#cd7f32 30%,#f29d49 50%,#cd7f32 70%,#f29d49 80%,#CD7F32 95%)' : // Bronze gradient
                                                '',
                                    backgroundColor: player.rank !== 1 && player.rank !== 2 && player.rank !== 3 ? (player.rank % 2 === 0 ? 'white' : '#dedede') : 'transparent',
                                    border: '2px solid #333',
                                    boxShadow: 'inset 0 0 10px rgba(0, 0, 0, 0.2)',
                                }}
                            >
                                <TableCell>{(player.fullName || '').trim() || `Player ${index + 1}`}</TableCell>
                                <TableCell>{player.firsts || 0}</TableCell>
                                <TableCell>{player.lasts || 0}</TableCell>
                                <TableCell>{player.totalScore || 0}</TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default GrandPrixScores;
